import type { UIConfigVariable } from 'types/ui-setup-config';
import { readVariableValue, normalizeCalcRef } from 'context/StructureProvider/helper';
import { cleanFloat } from 'utils/helper/numeric';
import { compileFormula, getFormulaReferences, isReservedFormulaName } from './formulaEngine';

export type InactiveCalcVariable = UIConfigVariable & {
  inputType: 'InactiveCalc';
  calculation: NonNullable<UIConfigVariable['calculation']>;
};

export type UnitConversion = (fromUnit: string, value: number, toUnit: string) => number;
export type CalculationParameter = Pick<UIConfigVariable, 'name' | 'element' | 'unit' | 'displayUnit' | 'arrays'>;

export type InactiveCalcValue = {
  baseValue: number | number[][];
  displayValue: number | number[][];
};

export type VariableRef = { name: string; element?: string };

type ColumnUnits = { unit: ReadonlyArray<string>; displayUnit: ReadonlyArray<string> };

type DependencyMeta = VariableRef & {
  // Where the value goes in the formula scope: `[alias]`, `[name]` or `[element, name]`.
  scopePath: [string] | [string, string];
  found: boolean;
  unit?: string;
  displayUnit?: string;
  // Per-column units of table-shaped parameters.
  columns?: ColumnUnits;
};

const columnUnitsOf = (parameter: CalculationParameter | undefined): ColumnUnits | undefined =>
  parameter?.arrays?.unit && parameter.arrays.displayUnit
    ? { unit: parameter.arrays.unit, displayUnit: parameter.arrays.displayUnit }
    : undefined;

const toDependencyMeta = (
  scopePath: DependencyMeta['scopePath'],
  ref: VariableRef,
  matching: CalculationParameter | undefined,
): DependencyMeta => ({
  scopePath,
  element: ref.element,
  name: ref.name,
  found: Boolean(matching),
  unit: matching?.unit,
  displayUnit: matching?.displayUnit,
  columns: columnUnitsOf(matching),
});

// Cached per variable object; a structure reload creates new objects, which drops the cache.
const dependencyMetaCache = new WeakMap<
  InactiveCalcVariable,
  { parameterList: ReadonlyArray<CalculationParameter>; dependencies: Array<DependencyMeta> }
>();

const sameRef = (parameter: CalculationParameter, ref: VariableRef) =>
  parameter.name === ref.name && (ref.element ?? undefined) === (parameter.element ?? undefined);

/**
 * Resolves what a calculation reads: aliases from `calculation.variables` that
 * the formula uses first, then parameters referenced directly in the formula.
 * Unused aliases are ignored. Direct references that mathjs defines (`pi`,
 * `phi`, `max`) or that match no parameter are left to mathjs.
 */
const getDependencies = (
  inactiveVariable: InactiveCalcVariable,
  parameterList: ReadonlyArray<CalculationParameter>,
): Array<DependencyMeta> => {
  const cached = dependencyMetaCache.get(inactiveVariable);
  if (cached?.parameterList === parameterList) return cached.dependencies;

  const references = getFormulaReferences(inactiveVariable.calculation.formula);
  const referencedSymbols = new Set(references.map((reference) => reference.symbol));
  const aliases = inactiveVariable.calculation.variables ?? {};
  const dependencies: Array<DependencyMeta> = Object.entries(aliases)
    .filter(([alias]) => referencedSymbols.has(alias))
    .map(([alias, rawRef]) => {
      const ref = normalizeCalcRef(rawRef);
      return toDependencyMeta(
        [alias],
        ref,
        parameterList.find((parameter) => sameRef(parameter, ref)),
      );
    });

  references.forEach((reference) => {
    if (reference.symbol in aliases || isReservedFormulaName(reference.symbol)) return;
    const ref: VariableRef =
      reference.kind === 'symbol'
        ? { name: reference.symbol }
        : { element: reference.symbol, name: reference.property };
    const matching = parameterList.find((parameter) => sameRef(parameter, ref));
    if (!matching) return;
    const scopePath: DependencyMeta['scopePath'] =
      reference.kind === 'symbol' ? [reference.symbol] : [reference.symbol, reference.property];
    dependencies.push(toDependencyMeta(scopePath, ref, matching));
  });

  dependencyMetaCache.set(inactiveVariable, { parameterList, dependencies });
  return dependencies;
};

/** Parameters a calculation reads, for dependency ordering and authoring checks. */
export const getCalculationDependencies = (
  inactiveVariable: InactiveCalcVariable,
  parameterList: ReadonlyArray<CalculationParameter>,
): Array<VariableRef> =>
  getDependencies(inactiveVariable, parameterList).map(({ element, name }) => ({ element, name }));

/* eslint-disable no-param-reassign */
const writeScope = (scope: Record<string, any>, path: [string] | [string, string], value: unknown) => {
  if (path.length === 1) {
    scope[path[0]] = value;
    return;
  }
  scope[path[0]] = { ...(scope[path[0]] ?? {}), [path[1]]: value };
};
/* eslint-enable no-param-reassign */

const convertTableColumns = (
  table: ReadonlyArray<ReadonlyArray<unknown>>,
  fromUnits: ReadonlyArray<string>,
  toUnits: ReadonlyArray<string>,
  unitConversion: UnitConversion,
): number[][] => {
  const needsConversion = fromUnits.some((unit, index) => toUnits[index] !== unit);
  return table.map((row) =>
    row.map((cell, index) => {
      if (typeof cell !== 'number') return cell as number;
      const fromUnit = fromUnits[index];
      const toUnit = toUnits[index];
      const converted =
        needsConversion && fromUnit !== undefined && toUnit !== undefined && fromUnit !== toUnit
          ? unitConversion(fromUnit, cell, toUnit)
          : cell;
      return cleanFloat(converted);
    }),
  );
};

// Scalars convert by the parameter's units, table rows column by column.
// Other shapes (booleans, strings, time-series rows) enter the scope as stored.
const toBaseUnits = (rawValue: unknown, dependency: DependencyMeta, unitConversion: UnitConversion): unknown => {
  if (typeof rawValue === 'number') {
    return dependency.displayUnit && dependency.displayUnit !== dependency.unit
      ? unitConversion(dependency.displayUnit, rawValue, dependency.unit ?? '')
      : rawValue;
  }
  const { columns } = dependency;
  if (!columns || !Array.isArray(rawValue)) return rawValue;
  const needsConversion = columns.unit.some((unit, index) => columns.displayUnit[index] !== unit);
  if (!needsConversion) return rawValue;
  return convertTableColumns(rawValue as number[][], columns.displayUnit, columns.unit, unitConversion);
};

/**
 * True when this InactiveCalc produces a table: `arrays` declares column names,
 * units and display units of the same non-zero length. The single predicate for
 * the engine, the form and the results page.
 */
export const isTableInactiveCalc = (variable: Pick<UIConfigVariable, 'arrays'>): boolean => {
  const columns = variable.arrays?.columnNames?.length ?? 0;
  return columns > 0 && variable.arrays?.unit?.length === columns && variable.arrays?.displayUnit?.length === columns;
};

const resultColumnCount = (variable: InactiveCalcVariable): number => variable.arrays?.columnNames?.length ?? 0;

/** Unwraps mathjs Matrix (and nested) values to plain JS arrays. */
const unwrapMatrix = (value: unknown): unknown => {
  if (value != null && typeof value === 'object' && typeof (value as { toArray?: unknown }).toArray === 'function') {
    return unwrapMatrix((value as { toArray: () => unknown }).toArray());
  }
  if (Array.isArray(value)) return value.map(unwrapMatrix);
  return value;
};

const isNumberVector = (value: unknown): value is number[] =>
  Array.isArray(value) && value.every((cell) => typeof cell === 'number');

const isFiniteNumberTable = (value: unknown, columnCount: number): value is number[][] => {
  if (!Array.isArray(value)) return false;
  return value.every(
    (row) =>
      Array.isArray(row) &&
      row.length === columnCount &&
      row.every((cell) => typeof cell === 'number' && Number.isFinite(cell)),
  );
};

/** Evaluates once and returns the configured base-unit and display-unit values. */
export const calculateInactiveInputValues = (
  inactiveVariable: InactiveCalcVariable,
  values: Record<string, any>,
  parameterList: ReadonlyArray<CalculationParameter>,
  unitConversion: UnitConversion,
): InactiveCalcValue | undefined => {
  if (!inactiveVariable.calculation?.formula) return undefined;

  const dependencies = getDependencies(inactiveVariable, parameterList);
  // Without unit metadata the value cannot be converted, so fail instead of guessing.
  if (dependencies.some((dependency) => !dependency.found)) return undefined;

  const scope: Record<string, any> = {};
  for (const dependency of dependencies) {
    const rawValue = readVariableValue(values, dependency.element, dependency.name);
    writeScope(scope, dependency.scopePath, toBaseUnits(rawValue, dependency, unitConversion));
  }

  try {
    const evaluated = unwrapMatrix(compileFormula(inactiveVariable.calculation.formula).evaluate(scope));

    if (isTableInactiveCalc(inactiveVariable)) {
      const columns = columnUnitsOf(inactiveVariable);
      const columnCount = resultColumnCount(inactiveVariable);
      // A vector result fills a single declared column, e.g. a merged time grid.
      const table = columnCount === 1 && isNumberVector(evaluated) ? evaluated.map((cell) => [cell]) : evaluated;
      if (!columns || columnCount === 0 || !isFiniteNumberTable(table, columnCount)) return undefined;

      // Same units in and out: a cleaned copy, so the draft never shares arrays with the evaluation.
      const baseValue = convertTableColumns(table, columns.unit, columns.unit, unitConversion);
      const displayValue = convertTableColumns(table, columns.unit, columns.displayUnit, unitConversion);
      return { baseValue, displayValue };
    }

    // Comparisons double as 0/1 flags for show and hide rules.
    const scalar = typeof evaluated === 'boolean' ? Number(evaluated) : evaluated;
    // Non-number and non-finite results (Infinity, NaN) count as failed.
    if (typeof scalar !== 'number' || !Number.isFinite(scalar)) return undefined;

    const converted =
      inactiveVariable.displayUnit && inactiveVariable.displayUnit !== inactiveVariable.unit
        ? unitConversion(inactiveVariable.unit ?? '', scalar, inactiveVariable.displayUnit)
        : scalar;
    if (!Number.isFinite(converted)) return undefined;

    return {
      baseValue: cleanFloat(scalar),
      displayValue: cleanFloat(converted),
    };
  } catch {
    return undefined;
  }
};

export const calculateInactiveInput = (
  inactiveVariable: InactiveCalcVariable,
  values: Record<string, any>,
  parameterList: ReadonlyArray<CalculationParameter>,
  unitConversion: UnitConversion,
): number | number[][] | undefined =>
  calculateInactiveInputValues(inactiveVariable, values, parameterList, unitConversion)?.displayValue;

export default calculateInactiveInput;
